import { Component, ElementRef, HostListener, Input, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { COMMA, ENTER, SPACE } from '@angular/cdk/keycodes';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';

@Component({
  selector: 'shared-chips-input',
  templateUrl: './chips-input.component.html',
  styleUrls: ['./chips-input.component.scss'],
  providers: [{ provide: NG_VALUE_ACCESSOR, multi: true, useExisting: ChipsInputComponent }],
  standalone: false,
})
export class ChipsInputComponent implements ControlValueAccessor {
  @Input() chipList: string[] = [];
  @Input() separatorKeysCodes: number[] = [ENTER, SPACE, COMMA];

  @ViewChild('chipInput', { static: true }) chipInput: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;

  @HostListener('window:keydown', ['$event']) handleKeyboardEvent(event: KeyboardEvent): void {
    if (document.activeElement === this.chipInput.nativeElement) {
      // submit on Ctrl + Enter
      if (event.ctrlKey && event.key === 'Enter') {
        this.onChange(this.chips);
      }
    }
  }

  get currentChip(): string {
    return this._currentChip;
  }

  set currentChip(value: string) {
    this._currentChip = value;
    this.filterChips(value);
  }

  private _currentChip = '';

  chips: string[] = [];
  filteredChips: string[];

  private onChange = (values: string[]): void => {};
  private onTouched = (): void => {};
  private touched = false;
  disabled = false;

  add(event: MatChipInputEvent): void {
    const value = event.value;

    if (!this.chips || !Array.isArray(this.chips)) {
      this.chips = [];
    }

    const trimmedValue = (value ?? '').trim();
    if (trimmedValue && this.chips?.includes(trimmedValue) === false) {
      this.chips.push(trimmedValue);
    }

    this.currentChip = '';
    this.onChange(this.chips);
  }

  remove(chip: string): void {
    const index = this.chips.indexOf(chip);
    if (index >= 0) {
      this.chips.splice(index, 1);
      this.onChange(this.chips);
      this.filterChips();
    }
    this.chipInput.nativeElement.focus();
    this.autocomplete.closePanel();
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    if (this.chips.includes(event.option.viewValue) === false) {
      this.chips.push(event.option.viewValue);
      this.onChange(this.chips);
    }
    this.currentChip = '';
    this.chipInput.nativeElement.value = '';
  }

  private filterChips(value?: string): void {
    if (!this.chipList) {
      this.filteredChips = [];
      return;
    }

    const filterValue = value?.toLowerCase();
    this.filteredChips = this.chipList.filter(i => !this.chips.includes(i) && (!filterValue || i.toLowerCase().startsWith(filterValue)));
  }

  writeValue(values: string[]): void {
    this.chips = values;
  }

  registerOnChange(onChange: (values: string[]) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  markAsTouched(): void {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }
}
